"use client";
import React from "react";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import { constants } from "../../../constants/pageConstants/pdpConstants";

/**
 * ImageZoom component that displays an image with optional zoom functionality and click handler.
 *
 * @date 17/07/2024
 * @param {Object} param - The component props.
 * @param {string} param.imageSrc - The source URL of the image to be displayed.
 * @param {Function} [param.onImageClick] - Optional callback function triggered when the image is clicked.
 * @param {string} [param.skuCode] - Optional SKU code related to the image.
 * @returns {JSX.Element} The rendered ImageZoom component.
 */
const ImageZoom = ({ imageSrc, onImageClick, skuCode }) => {
  return (
    <div className="relative flex" id="imageZoomContainer">
      <div>
        <div
          id="original-image-wrapper"
          role="presentation"
          onClick={onImageClick}
          className="relative aspect-square w-full max-w-[800px]"
        >
          <CustomImage
            id="original-image"
            src={imageSrc}
            width={800}
            height={800}
            isPriority={true}
            alt="pdp_main_image"
            sizes="(max-width: 768px) 100vw, 50vw"
            itemProp="image"
            className="h-full w-full cursor-zoom-in rounded-[16px] object-cover"
          />
        </div>
        <div className="mt-8 flex w-full">
          <Typography
            id="product-sku-code"
            Tag={"p"}
            variant="p"
            classes="text-12 font-400 text-grey-400 italic basis-1/3 flex-grow"
          >
            {skuCode || ""}
          </Typography>
          <Typography
            id="image_magnifier_text"
            Tag={"p"}
            variant="p"
            classes=" text-12 font-400 text-grey-500 basis-2/3 flex-grow"
          >
            {constants.CLICK_TO_OPEN_EXPANDED_VIEW || ""}
          </Typography>
        </div>
      </div>
    </div>
  );
};

ImageZoom.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  onImageClick: PropTypes.func,
  skuCode: PropTypes.string,
};

export default React.memo(ImageZoom);
